<template>
  <div class="nivelaciones-root">

    <!-- ── Header y Filtros Inline (Todo en una sola fila) ─────────── -->
    <div class="dialog-header px-6 py-3.5 border-bottom d-flex align-center justify-space-between flex-wrap gap-3">
      
      <!-- Título con Icono -->
      <div class="d-flex align-center flex-shrink-0">
        <div class="header-icon-box mr-3">
          <v-icon color="primary" size="22">mdi-file-certificate-outline</v-icon>
        </div>
        <div>
          <div class="d-flex align-center gap-2">
            <span class="header-title">Solicitudes de Nivelación</span>
            <span v-if="!cargando && totalRegistros > 0" class="header-count-badge">
              {{ totalRegistros }}
            </span>
          </div>
          <div class="header-subtitle mt-0.5">
            Gestión y seguimiento de nivelaciones
          </div>
        </div>
      </div>

      <!-- Controles Inline: Buscador + Tabs de Estado + Acciones -->
      <div class="d-flex align-center flex-wrap gap-2.5">
        <!-- Buscador -->
        <div class="search-input-box">
          <v-text-field
            v-model="busqueda"
            placeholder="Buscar estudiante, CI, programa..."
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            class="rounded-lg custom-input"
            @input="buscar"
            @click:clear="limpiarBusqueda" />
        </div>

        <!-- Filtros Rápidos por Estado (Tabs / Pills) -->
        <div class="status-tabs-container d-flex align-center">
          <button
            v-for="tab in estadosSelect"
            :key="tab.value ?? 'todos'"
            type="button"
            :class="['status-tab-btn', { 'status-tab-btn--active': estadoSolicitud === tab.value }]"
            @click="seleccionarEstado(tab.value)">
            <span
              v-if="tab.value"
              :class="['status-dot', `status-dot--${tab.value}`]" />
            <span>{{ tab.text }}</span>
          </button>
        </div>

        <!-- Acciones (Limpiar y Recargar) -->
        <div class="toolbar-actions d-flex align-center gap-1.5">
          <!-- Botón Limpiar Filtros -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                depressed
                outlined
                color="grey darken-1"
                class="rounded-lg action-btn clear-filter-btn"
                :disabled="!hayFiltros || cargando"
                @click="limpiarFiltros">
                <v-icon small left>mdi-filter-off-outline</v-icon>
                Limpiar
              </v-btn>
            </template>
            <span>Restablecer filtros de búsqueda</span>
          </v-tooltip>

          <!-- Botón Recargar -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                depressed
                color="primary"
                class="rounded-lg action-btn reload-btn"
                :loading="cargando"
                @click="listarRestableciendoPagina">
                <v-icon :class="{ 'spin-animation': cargando }">mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Recargar lista</span>
          </v-tooltip>
        </div>
      </div>

    </div>

    <!-- ── Loading Skeleton State ───────────────────────────────────── -->
    <div v-if="cargando" class="px-6 py-5">
      <div class="skeleton-table-wrapper">
        <div class="skeleton-header d-flex align-center justify-space-between px-4 py-3 border-bottom">
          <div class="skeleton-shimmer skeleton-title" style="width: 140px; height: 16px;" />
          <div class="skeleton-shimmer skeleton-title" style="width: 80px; height: 16px;" />
        </div>
        <div v-for="n in 5" :key="n" class="skeleton-row d-flex align-center px-4 py-3.5 border-bottom gap-3">
          <div class="flex-grow-1">
            <div class="skeleton-shimmer mb-1.5" style="width: 45%; height: 14px;" />
            <div class="skeleton-shimmer" style="width: 30%; height: 11px;" />
          </div>
          <div class="skeleton-shimmer" style="width: 55px; height: 26px; border-radius: 6px;" />
          <div class="skeleton-shimmer" style="width: 140px; height: 14px;" />
          <div class="skeleton-shimmer" style="width: 80px; height: 22px; border-radius: 9999px;" />
          <div class="skeleton-shimmer" style="width: 70px; height: 28px; border-radius: 6px;" />
        </div>
      </div>
    </div>

    <!-- ── Tabla y Contenido ────────────────────────────────────────── -->
    <div v-else class="px-6 pt-4 pb-6">

      <!-- Estado Vacío -->
      <div v-if="!solicitudes.length" class="empty-state-box">
        <div class="empty-state-icon-wrap mb-3">
          <v-icon size="36" color="primary">mdi-file-search-outline</v-icon>
        </div>
        <div class="empty-state-title">No se encontraron solicitudes de nivelación</div>
        <p class="empty-state-subtitle">
          {{ hayFiltros
            ? 'No hay registros que coincidan con los filtros aplicados. Intenta restablecer los filtros de búsqueda.'
            : 'Actualmente no existen solicitudes de nivelación registradas en el sistema.' }}
        </p>
        <v-btn
          v-if="hayFiltros"
          depressed
          outlined
          color="primary"
          class="rounded-lg mt-2 action-btn"
          @click="limpiarFiltros">
          <v-icon small left>mdi-filter-off-outline</v-icon>
          Restablecer Filtros
        </v-btn>
      </div>

      <!-- Tabla de Datos -->
      <template v-else>
        <div class="table-container">
          <table class="grades-table w-100">
            <thead>
              <tr>
                <th class="th-numero text-center">#</th>
                <th class="th-estudiante text-left">ESTUDIANTE</th>
                <th class="th-nota text-center">NOTA</th>
                <th class="th-programa text-left">PROGRAMA / SEDE</th>
                <th class="th-modulo text-left">MÓDULO / ESTADO</th>
                <th class="th-persona text-left">CONTADORA</th>
                <th class="th-persona text-left">SOLICITADO POR</th>
                <th class="th-persona text-left">REVISADO POR</th>
                <th class="th-acciones text-center">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(solicitud, index) in solicitudes" :key="solicitud.id" class="table-data-row">
                
                <!-- # Numeración -->
                <td class="td-numero text-center">
                  <span class="row-index-badge">
                    {{ (pagina - 1) * itemsPorPagina + index + 1 }}
                  </span>
                </td>

                <!-- Estudiante -->
                <td class="td-estudiante text-left">
                  <div class="student-info-block">
                    <span class="student-name" :title="solicitud.estudiante?.nombreCompleto || '—'">
                      {{ solicitud.estudiante?.nombreCompleto || '—' }}
                    </span>
                    <span class="student-ci mt-0.5">
                      <v-icon x-small color="grey darken-1" class="mr-1">mdi-card-account-details-outline</v-icon>
                      <span>CI: {{ solicitud.estudiante?.ci || '—' }}</span>
                    </span>
                  </div>
                </td>

                <!-- Nota -->
                <td class="td-nota text-center">
                  <div class="nota-card-box">
                    <span class="nota-value">{{ solicitud.nota }}</span>
                    <span
                      v-if="solicitud.notaAnterior !== null && solicitud.notaAnterior !== undefined"
                      class="nota-anterior-badge"
                      :title="'Nota anterior: ' + solicitud.notaAnterior">
                      <v-icon x-small color="grey" class="mr-0.5">mdi-history</v-icon>
                      antes {{ solicitud.notaAnterior }}
                    </span>
                  </div>
                </td>

                <!-- Programa / Sede -->
                <td class="td-programa text-left">
                  <span class="programa-nombre" :title="solicitud.programa?.nombreCompuesto || '—'">
                    {{ solicitud.programa?.nombreCompuesto || '—' }}
                  </span>
                  <div class="d-flex align-center flex-wrap gap-1 mt-1">
                    <span v-if="solicitud.programa?.codigo" class="micro-chip chip-code">
                      <v-icon x-small color="primary" class="mr-0.5">mdi-barcode</v-icon>
                      {{ solicitud.programa.codigo }}
                    </span>
                    <span v-if="solicitud.sede?.nombre" class="micro-chip chip-sede">
                      <v-icon x-small color="teal darken-2" class="mr-0.5">mdi-map-marker-outline</v-icon>
                      {{ solicitud.sede.nombre }}
                    </span>
                  </div>
                </td>

                <!-- Módulo / Estado -->
                <td class="td-modulo text-left">
                  <div v-if="solicitud.programaModulo" class="modulo-title-block">
                    <span class="modulo-number-badge mr-1">M{{ solicitud.programaModulo.numero }}</span>
                    <span class="modulo-title" :title="solicitud.programaModulo.moduloNombre">
                      {{ solicitud.programaModulo.moduloNombre }}
                    </span>
                  </div>
                  <span v-else class="text-muted text-caption">—</span>

                  <div class="mt-1.5">
                    <span :class="['custom-badge', getEstadoClass(solicitud.estadoSolicitud)]">
                      <v-icon x-small :color="getEstadoIconColor(solicitud.estadoSolicitud)" class="mr-1">
                        {{ getEstadoIcon(solicitud.estadoSolicitud) }}
                      </v-icon>
                      {{ estadoLabel(solicitud.estadoSolicitud) }}
                    </span>
                  </div>
                </td>

                <!-- Contadora -->
                <td class="td-persona text-left">
                  <template v-if="solicitud.contadora">
                    <div class="persona-block">
                      <span class="persona-nombre" :title="solicitud.contadora.nombreCompleto">
                        {{ solicitud.contadora.nombreCompleto }}
                      </span>
                      <small class="persona-ci">
                        <v-icon x-small color="grey lighten-1" class="mr-0.5">mdi-identifier</v-icon>
                        CI: {{ solicitud.contadora.ci }}
                      </small>
                    </div>
                  </template>
                  <span v-else class="text-muted text-caption font-italic">—</span>
                </td>

                <!-- Solicitado por -->
                <td class="td-persona text-left">
                  <template v-if="solicitud.solicitadoPor">
                    <div class="persona-block">
                      <span class="persona-nombre" :title="solicitud.solicitadoPor.nombreCompleto">
                        {{ solicitud.solicitadoPor.nombreCompleto }}
                      </span>
                      <small class="persona-ci">
                        <v-icon x-small color="grey lighten-1" class="mr-0.5">mdi-identifier</v-icon>
                        CI: {{ solicitud.solicitadoPor.ci }}
                      </small>
                      <span class="persona-fecha mt-0.5">
                        <v-icon x-small color="blue-grey lighten-1" class="mr-0.5">mdi-calendar-clock</v-icon>
                        {{ formatFecha(solicitud.fechaSolicitud) }}
                      </span>
                    </div>
                  </template>
                  <span v-else class="text-muted text-caption font-italic">—</span>
                </td>

                <!-- Revisado por -->
                <td class="td-persona text-left">
                  <template v-if="solicitud.revisadoPor">
                    <div class="persona-block">
                      <span class="persona-nombre" :title="solicitud.revisadoPor.nombreCompleto">
                        {{ solicitud.revisadoPor.nombreCompleto }}
                      </span>
                      <small class="persona-ci">
                        <v-icon x-small color="grey lighten-1" class="mr-0.5">mdi-identifier</v-icon>
                        CI: {{ solicitud.revisadoPor.ci }}
                      </small>
                      <span class="persona-fecha persona-fecha--reviewed mt-0.5">
                        <v-icon x-small color="teal lighten-1" class="mr-0.5">mdi-calendar-check</v-icon>
                        {{ formatFecha(solicitud.fechaRevision) }}
                      </span>
                    </div>
                  </template>
                  <template v-else>
                    <span class="no-revisor-chip">
                      <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                      Sin revisar
                    </span>
                  </template>
                </td>

                <!-- Acciones -->
                <td class="td-acciones text-center">
                  <div class="action-buttons-group d-flex flex-column gap-1.5">
                    <v-btn
                      depressed
                      color="primary"
                      class="rounded-lg table-btn table-btn--primary w-100"
                      @click="verSolicitud(solicitud)">
                      <v-icon x-small left>mdi-eye-outline</v-icon>
                      Ver
                    </v-btn>

                    <v-btn
                      v-if="solicitud.estadoSolicitud === 'pendiente' && puedeRevisarSolicitud"
                      depressed
                      color="warning"
                      class="rounded-lg table-btn table-btn--warning w-100"
                      @click="revisarSolicitud(solicitud)">
                      <v-icon x-small left>mdi-clipboard-check-outline</v-icon>
                      Revisar
                    </v-btn>

                    <v-btn
                      v-if="solicitud.estadoSolicitud === 'aprobado' && puedeRevisarSolicitud"
                      depressed
                      color="info"
                      :loading="sincronizando === solicitud.id"
                      :disabled="!!sincronizando"
                      class="rounded-lg table-btn table-btn--info w-100"
                      @click="sincronizarUniversidad(solicitud)">
                      <v-icon x-small left>mdi-sync</v-icon>
                      Sincronizar Universidad
                    </v-btn>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <!-- ── Footer & Paginación ──────────────────────────────────── -->
        <div class="pagination-footer d-flex align-center justify-space-between flex-wrap mt-4 px-2 gap-3">
          <!-- Info de Rango y Selector de Items -->
          <div class="d-flex align-center flex-wrap gap-3">
            <span class="pagination-info-text">
              {{ textoPaginacion }}
            </span>
            <div class="d-flex align-center items-per-page-wrap">
              <span class="text-caption grey--text text--darken-1 mr-2">Mostrar:</span>
              <v-select
                :items="[10, 25, 50, 100]"
                v-model="itemsPorPagina"
                outlined
                dense
                hide-details
                class="rounded-lg custom-input select-page-size"
                @change="cambiarPagina" />
            </div>
          </div>

          <!-- Componente de Paginación -->
          <v-pagination
            v-model="pagina"
            :length="metadatos?.totalPaginas || 1"
            :total-visible="7"
            color="primary"
            class="custom-pagination"
            @input="listar" />
        </div>
      </template>
    </div>

    <!-- ── Diálogos y Notificaciones ────────────────────────────────── -->
    <dialog-revisar-solicitud-nivelacion
      ref="dialogRevisar"
      :puede-revisar-solicitud="puedeRevisarSolicitud"
      @actualizado="listar" />

    <!-- Errores de sincronización con universidad -->
    <v-dialog v-model="dialogErroresSincronizacion" max-width="480px" content-class="rounded-lg">
      <v-card>
        <v-card-title class="d-flex align-center px-6 py-4 border-bottom">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          <span class="text-h6 font-weight-bold">Error al sincronizar</span>
        </v-card-title>
        <v-card-text class="px-6 py-4">
          <ul class="errores-sincronizacion-list">
            <li v-for="(error, index) in erroresSincronizacion" :key="index">{{ error }}</li>
          </ul>
        </v-card-text>
        <v-card-actions class="px-6 py-4 justify-end border-top">
          <v-btn depressed color="primary" class="rounded-lg action-btn px-6" @click="dialogErroresSincronizacion = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <notifications group="notifications" position="top right" />
  </div>
</template>

<script>
import SolicitudCalificacionNivelacionService from '../../services/solicitud-calificacion-nivelacion.service';
import AccesosService from '../../services/accesos.service';
import DialogRevisarSolicitudNivelacion from '../components/DialogRevisarSolicitudNivelacion.vue';
import {
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_LABELS,
  ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_COLORES,
} from '../../utils/constantes';

const ESTADOS_SELECT = [
  { value: null, text: 'Todos' },
  { value: 'pendiente', text: 'Pendientes' },
  { value: 'aprobado', text: 'Aprobados' },
  { value: 'rechazado', text: 'Rechazados' },
  { value: 'generado', text: 'Generados' },
];

export default {
  name: 'NivelacionesLayout',
  components: { DialogRevisarSolicitudNivelacion },
  data() {
    return {
      solicitudes: [],
      metadatos: null,
      cargando: false,
      pagina: 1,
      itemsPorPagina: Number(localStorage.getItem(`${this.$route.name}-itemsPorPagina`)) || 10,
      busqueda: '',
      estadoSolicitud: null,
      retardo: null,
      estadosSelect: ESTADOS_SELECT,
      puedeRevisarSolicitud: false,
      sincronizando: null,
      dialogErroresSincronizacion: false,
      erroresSincronizacion: [],
    };
  },
  computed: {
    hayFiltros() {
      return Boolean(this.busqueda?.trim()) || this.estadoSolicitud !== null;
    },
    totalRegistros() {
      return this.metadatos?.total || this.metadatos?.totalRegistros || this.solicitudes.length;
    },
    textoPaginacion() {
      if (!this.solicitudes.length) return '';
      const total = this.metadatos?.total || this.metadatos?.totalRegistros || null;
      const inicio = (this.pagina - 1) * this.itemsPorPagina + 1;
      const fin = Math.min(this.pagina * this.itemsPorPagina, total || (inicio + this.solicitudes.length - 1));
      if (total) {
        return `Mostrando ${inicio} - ${fin} de ${total} solicitudes`;
      }
      return `Página ${this.pagina} de ${this.metadatos?.totalPaginas || 1}`;
    },
  },
  created() {
    this.listar();
    this.verificarAccesoDna();
  },
  methods: {
    async verificarAccesoDna() {
      try {
        const { data } = await AccesosService.verificarAccesoDna();
        this.puedeRevisarSolicitud = Boolean(data?.datos?.permiso);
      } catch (error) {
        this.puedeRevisarSolicitud = false;
      }
    },
    estadoLabel(estado) {
      return ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_LABELS[estado] || estado || '—';
    },
    estadoColor(estado) {
      return ESTADOS_SOLICITUD_CALIFICACION_NIVELACION_COLORES[estado] || 'grey';
    },
    getEstadoClass(estado) {
      if (!estado) return 'badge-default';
      const norm = estado.toLowerCase().trim();
      if (norm === 'aprobado' || norm === 'aprobada') return 'badge-aprobado';
      if (norm === 'pendiente') return 'badge-pendiente';
      if (norm === 'generado') return 'badge-generado';
      if (norm === 'rechazado' || norm === 'rechazada') return 'badge-rechazado';
      return 'badge-default';
    },
    getEstadoIcon(estado) {
      if (!estado) return 'mdi-help-circle-outline';
      const norm = estado.toLowerCase().trim();
      if (norm === 'aprobado' || norm === 'aprobada') return 'mdi-check-circle';
      if (norm === 'pendiente') return 'mdi-clock-outline';
      if (norm === 'generado') return 'mdi-file-document-edit-outline';
      if (norm === 'rechazado' || norm === 'rechazada') return 'mdi-close-circle';
      return 'mdi-information-outline';
    },
    getEstadoIconColor(estado) {
      if (!estado) return 'grey';
      const norm = estado.toLowerCase().trim();
      if (norm === 'aprobado' || norm === 'aprobada') return 'success';
      if (norm === 'pendiente') return 'orange darken-2';
      if (norm === 'generado') return 'primary';
      if (norm === 'rechazado' || norm === 'rechazada') return 'error';
      return 'grey';
    },
    getIniciales(nombre) {
      if (!nombre) return 'ES';
      const partes = nombre.trim().split(/\s+/);
      if (partes.length === 1) return partes[0].substring(0, 2).toUpperCase();
      return (partes[0][0] + partes[1][0]).toUpperCase();
    },
    formatFecha(fecha) {
      if (!fecha) return '—';
      return new Date(fecha).toLocaleString('es-BO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    buscar(q) {
      clearTimeout(this.retardo);
      this.retardo = setTimeout(() => {
        this.busqueda = q;
        this.listarRestableciendoPagina();
      }, 400);
    },
    limpiarBusqueda() {
      this.busqueda = '';
      this.listarRestableciendoPagina();
    },
    seleccionarEstado(estado) {
      if (this.estadoSolicitud === estado) return;
      this.estadoSolicitud = estado;
      this.listarRestableciendoPagina();
    },
    limpiarFiltros() {
      this.busqueda = '';
      this.estadoSolicitud = null;
      this.listarRestableciendoPagina();
    },
    listarRestableciendoPagina() {
      if (this.pagina !== 1) {
        this.pagina = 1;
      }
      this.listar();
    },
    cambiarPagina() {
      this.listarRestableciendoPagina();
      localStorage.setItem(`${this.$route.name}-itemsPorPagina`, this.itemsPorPagina);
    },
    verSolicitud(solicitud) {
      this.$refs.dialogRevisar.abrir(solicitud.id, true);
    },
    async sincronizarUniversidad(solicitud) {
      const confirmed = await this.$dialog.confirm(
        {
          title: 'Confirmar sincronización',
          body: '¿Está seguro de que desea sincronizar esta solicitud de nivelación con universidad?',
        },
        { okText: 'Sí', cancelText: 'No' },
      ).then(() => true).catch(() => false);

      if (!confirmed) return;

      this.sincronizando = solicitud.id;
      try {
        await SolicitudCalificacionNivelacionService.revisar({
          solicitudId: solicitud.id,
          estadoSolicitud: 'aprobado',
        });
        this.$notify({
          group: 'notifications', title: 'Éxito', text: 'Solicitud sincronizada correctamente', type: 'success',
        });
        this.listar();
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al sincronizar la solicitud';
        this.erroresSincronizacion = Array.isArray(mensaje) ? mensaje : [mensaje];
        this.dialogErroresSincronizacion = true;
      } finally {
        this.sincronizando = null;
      }
    },
    revisarSolicitud(solicitud) {
      this.$refs.dialogRevisar.abrir(solicitud.id);
    },
    async listar() {
      this.cargando = true;
      try {
        const { data } = await SolicitudCalificacionNivelacionService.listar({
          pagina: this.pagina,
          itemsPorPagina: this.itemsPorPagina,
          q: this.busqueda?.trim() || undefined,
          estadoSolicitud: this.estadoSolicitud || undefined,
        });
        this.solicitudes = data?.datos || [];
        this.metadatos = data?.paginacion || null;
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al obtener las solicitudes';
        this.solicitudes = [];
        this.$notify({ group: 'notifications', title: 'Error', text: mensaje, type: 'error' });
      } finally {
        this.cargando = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Raíz y Contenedor Principal ────────────────────────────────── */
.nivelaciones-root {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin: 0 4px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
}

/* ── Header ─────────────────────────────────────────────────────── */
.dialog-header {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
  border-bottom: 1px solid #e2e8f0;
}

.header-icon-box {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 10px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bfdbfe;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.08);
}

.header-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.header-count-badge {
  font-size: 0.7rem;
  font-weight: 700;
  background-color: #eff6ff;
  color: #2563eb;
  padding: 1px 7px;
  border-radius: 9999px;
  border: 1px solid #dbeafe;
}

.header-subtitle {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 400;
  line-height: 1.2;
}

/* ── Status Tabs (Pills) ────────────────────────────────────────── */
.status-tabs-container {
  display: flex;
  align-items: center;
  background-color: #f1f5f9;
  padding: 3px;
  border-radius: 9px;
  gap: 2px;
  border: 1px solid #e2e8f0;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.status-tabs-container::-webkit-scrollbar {
  display: none;
}

.status-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  user-select: none;
}

.status-tab-btn:hover {
  color: #1e293b;
  background-color: rgba(255, 255, 255, 0.6);
}

.status-tab-btn--active {
  background-color: #ffffff !important;
  color: #0f172a !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot--pendiente {
  background-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
}

.status-dot--aprobado {
  background-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.status-dot--rechazado {
  background-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.status-dot--generado {
  background-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* ── Buscador ───────────────────────────────────────────────────── */
.search-input-box {
  width: 240px;
}

.custom-input ::v-deep .v-input__slot {
  background-color: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;
  min-height: 34px !important;
}

.custom-input ::v-deep .v-input__slot:hover {
  border-color: #94a3b8 !important;
}

.custom-input ::v-deep .v-input__slot:focus-within {
  background-color: #ffffff !important;
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
}

.custom-input ::v-deep fieldset {
  border: none !important;
}

.custom-input ::v-deep .v-input__prepend-inner {
  margin-top: 5px !important;
}

.custom-input ::v-deep .v-input__append-inner {
  margin-top: 5px !important;
}

.custom-input ::v-deep .v-icon {
  color: #64748b !important;
  font-size: 18px !important;
}

.custom-input ::v-deep input {
  color: #0f172a !important;
  font-size: 0.8rem !important;
  font-weight: 500 !important;
}

.custom-input ::v-deep .v-select__selection {
  color: #0f172a !important;
  font-size: 0.8rem !important;
  font-weight: 500 !important;
}

/* ── Botones de Acción ──────────────────────────────────── */
.action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.1px !important;
  height: 34px !important;
  border-radius: 8px !important;
}

.clear-filter-btn {
  border-color: #cbd5e1 !important;
  color: #475569 !important;
}

.clear-filter-btn:hover:not(:disabled) {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
}

.reload-btn {
  min-width: 34px !important;
  width: 34px !important;
  padding: 0 !important;
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

/* ── Skeleton Loading ───────────────────────────────────────────── */
.skeleton-table-wrapper {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-circle {
  border-radius: 50% !important;
}

/* ── Estado Vacío ───────────────────────────────────────────────── */
.empty-state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 40px 20px;
  background: linear-gradient(180deg, #fafafa 0%, #f8fafc 100%);
  border: 1.5px dashed #cbd5e1;
  border-radius: 14px;
  text-align: center;
}

.empty-state-icon-wrap {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.empty-state-subtitle {
  font-size: 0.825rem;
  color: #64748b;
  max-width: 440px;
  margin-bottom: 0;
  line-height: 1.4;
}

/* ── Tabla de Solicitudes ────────────────────────────────────────── */
.table-container {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow-x: auto;
}

.grades-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.grades-table thead th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.725rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.06em !important;
  border-bottom: 1px solid #cbd5e1 !important;
  height: 44px !important;
  padding: 8px 12px;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.table-data-row {
  transition: background-color 0.15s ease;
}

.table-data-row:nth-child(odd) {
  background-color: #ffffff;
}

.table-data-row:nth-child(even) {
  background-color: #fafbfd;
}

.table-data-row:hover {
  background-color: #eff6ff !important;
}

.grades-table tbody td {
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 10px 12px;
  color: #334155;
  font-size: 0.815rem;
  vertical-align: middle;
}

.grades-table tbody tr:last-child td {
  border-bottom: none !important;
}

/* ── Columnas Específicas ───────────────────────────────────────── */
.td-numero {
  width: 44px;
}

.row-index-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background-color: #f1f5f9;
  color: #64748b;
  font-size: 0.725rem;
  font-weight: 600;
}

/* Estudiante */
.th-estudiante, .td-estudiante {
  min-width: 230px;
}

.avatar-initials {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e0e7ff 0%, #dbeafe 100%);
  color: #3730a3;
  border: 1px solid #c7d2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.student-info-block {
  overflow: hidden;
}

.student-name {
  display: block;
  font-weight: 600;
  color: #0f172a;
  font-size: 0.84rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.student-ci {
  display: flex;
  align-items: center;
  font-size: 0.725rem;
  color: #64748b;
  font-weight: 500;
}

/* Nota */
.td-nota {
  min-width: 80px;
  white-space: nowrap;
}

.nota-card-box {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  min-width: 54px;
}

.nota-value {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
}

.nota-anterior-badge {
  display: flex;
  align-items: center;
  font-size: 0.65rem;
  color: #94a3b8;
  font-weight: 600;
  margin-top: 2px;
}

/* Programa */
.th-programa, .td-programa {
  min-width: 200px;
  max-width: 320px;
}

.programa-nombre {
  display: block;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.8rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.micro-chip {
  display: inline-flex;
  align-items: center;
  font-size: 0.685rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 5px;
}

.chip-code {
  background-color: #eff6ff;
  color: #2563eb;
  border: 1px solid #dbeafe;
}

.chip-sede {
  background-color: #f0fdfa;
  color: #0f766e;
  border: 1px solid #ccfbf1;
}

/* Módulo */
.th-modulo, .td-modulo {
  min-width: 180px;
  max-width: 280px;
}

.modulo-title-block {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.modulo-number-badge {
  font-size: 0.685rem;
  font-weight: 700;
  background-color: #f1f5f9;
  color: #475569;
  padding: 1px 5px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.modulo-title {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Personas */
.td-persona {
  min-width: 140px;
  max-width: 200px;
}

.persona-block {
  display: flex;
  flex-direction: column;
}

.persona-nombre {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.persona-ci {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 0.715rem;
  font-weight: 500;
}

.persona-fecha {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 500;
}

.persona-fecha--reviewed {
  color: #0f766e;
  font-weight: 600;
}

.no-revisor-chip {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  color: #94a3b8;
  font-style: italic;
  padding: 2px 6px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  background-color: #fafafa;
}

/* Acciones */
.td-acciones {
  min-width: 100px;
}

.table-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  height: 28px !important;
  font-size: 0.725rem !important;
  letter-spacing: 0.1px !important;
  box-shadow: none !important;
}

.table-btn--primary {
  background: #eff6ff !important;
  color: #1d4ed8 !important;
  border: 1px solid #bfdbfe !important;
}

.table-btn--primary:hover {
  background: #2563eb !important;
  color: #ffffff !important;
  border-color: #2563eb !important;
}

.table-btn--warning {
  background: #fffbeb !important;
  color: #b45309 !important;
  border: 1px solid #fde68a !important;
}

.table-btn--warning:hover {
  background: #f59e0b !important;
  color: #ffffff !important;
  border-color: #f59e0b !important;
}

.table-btn--info {
  background: #ecfeff !important;
  color: #0e7490 !important;
  border: 1px solid #a5f3fc !important;
}

.table-btn--info:hover {
  background: #06b6d4 !important;
  color: #ffffff !important;
  border-color: #06b6d4 !important;
}

/* ── Badges de Estado ────────────────────────────────────────────── */
.custom-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 0.725rem;
  font-weight: 600;
  border-radius: 9999px;
  line-height: 1.25;
  white-space: nowrap;
}

.badge-aprobado {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.badge-pendiente {
  background-color: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.badge-rechazado {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.badge-generado {
  background-color: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.badge-default {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

/* ── Paginación Footer ──────────────────────────────────────────── */
.pagination-footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 12px;
}

.pagination-info-text {
  font-size: 0.8rem;
  color: #475569;
  font-weight: 600;
}

.items-per-page-wrap {
  background-color: #f8fafc;
  padding: 2px 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.select-page-size {
  max-width: 75px !important;
}

.select-page-size ::v-deep .v-input__slot {
  min-height: 28px !important;
  background-color: #ffffff !important;
  padding: 0 6px !important;
}

.custom-pagination {
  margin: 0 !important;
}

.custom-pagination ::v-deep .v-pagination__item {
  box-shadow: none !important;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.8rem;
  min-width: 32px;
  height: 32px;
  font-weight: 600;
  color: #475569;
  transition: all 0.15s ease;
}

.custom-pagination ::v-deep .v-pagination__item:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #0f172a;
}

.custom-pagination ::v-deep .v-pagination__item--active {
  background-color: #2563eb !important;
  color: #ffffff !important;
  border-color: #2563eb !important;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25) !important;
}

.custom-pagination ::v-deep .v-pagination__navigation {
  box-shadow: none !important;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 32px;
  height: 32px;
}

/* Helpers */
.gap-1 { gap: 4px; }
.gap-1\.5 { gap: 6px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

.errores-sincronizacion-list {
  margin: 0;
  padding-left: 20px;
  color: #b91c1c;
}

.errores-sincronizacion-list li {
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 6px;
}

.errores-sincronizacion-list li:last-child {
  margin-bottom: 0;
}
</style>

